import * as XLSX from 'xlsx';
import { resolvePlainColumnAliases } from '../components/chartBuilder/LiveDatasetTable';
import { applyCalculatedColumns, normalizeCalculatedColumns } from './calculatedColumns';

const INVALID_SHEET_CHARS = /[:\\/?*[\]]/g;
const MAX_SHEET_NAME_LENGTH = 31;
const RESERVED_SHEET_NAMES = new Set(['history']);

// Los gráficos tipo Tabla cargan el dataset completo en el navegador y lo paginan
// solo visualmente (ver DataTable.js, rowsPerPage por defecto = 100), a diferencia
// de los gráficos de línea/barra que ya vienen acotados desde la consulta SQL. Por
// ahora exportamos solo lo que se ve en la primera página, no el dataset completo.
const TABLE_CHART_EXPORT_LIMIT = 100;

/** Recorta las filas de un gráfico tipo Tabla a lo que se ve en pantalla por defecto. */
export function visibleRowsForExport(chart, rows) {
  const list = Array.isArray(rows) ? rows : [];
  if ((chart?.type === 'table' || chart?.type === 'tablePlus') && list.length > TABLE_CHART_EXPORT_LIMIT) {
    return list.slice(0, TABLE_CHART_EXPORT_LIMIT);
  }
  return list;
}

/** Sanea un nombre para usarlo como pestaña de Excel: sin caracteres inválidos, máximo 31 caracteres y único dentro del libro. */
export function sanitizeSheetName(name, usedNames) {
  let base = String(name || '').replace(INVALID_SHEET_CHARS, ' ').replace(/\s+/g, ' ').trim();
  if (!base || RESERVED_SHEET_NAMES.has(base.toLowerCase())) base = 'Grafico';
  base = base.slice(0, MAX_SHEET_NAME_LENGTH).replace(/^'+|'+$/g, '').trim() || 'Grafico';

  let candidate = base;
  let suffix = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const suffixText = ` (${suffix})`;
    candidate = `${base.slice(0, MAX_SHEET_NAME_LENGTH - suffixText.length)}${suffixText}`;
    suffix += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

function sanitizeFileName(name) {
  const cleaned = String(name || '').replace(/[\\/:*?"<>|]/g, '_').trim();
  return cleaned || 'export';
}

function chartTitle(chart) {
  return chart?.advancedConfig?.title || chart?.label || 'Grafico';
}

/** Mapa clave técnica del resultado -> nombre amigable, a partir de la config del gráfico guardado. */
function friendlyHeaderMapForChart(chart) {
  const map = {};
  if (chart?.type === 'table' || chart?.type === 'tablePlus') {
    const cols = chart?.visualizationSpec?.tableColumns || [];
    const aliases = resolvePlainColumnAliases(cols);
    cols.forEach((col, i) => {
      const key = aliases[i];
      if (!key) return;
      const label = (col?.alias && String(col.alias).trim()) || col?.column;
      if (label) map[key] = label;
    });
    normalizeCalculatedColumns(chart?.advancedConfig?.tableCalculatedColumns).forEach((def) => {
      if (def.key && def.label) map[def.key] = def.label;
    });
    return map;
  }
  // El servidor resuelve esto en mapSpecToChartConfig (columnLabels), con el alias real
  // de dimension/measure/series ya emparejado contra xAlias/yAlias/labelAlias; el cliente
  // solo recibe tableColumns (sin esa distincion fila/serie), asi que no se puede reconstruir
  // esta etiqueta por su cuenta aca sin adivinar.
  const columnLabels = chart?.columnLabels && typeof chart.columnLabels === 'object' ? chart.columnLabels : {};
  if (chart?.xColumn) {
    map[chart.xColumn] = chart?.advancedConfig?.xAxisTitle || columnLabels[chart.xColumn] || chart.xColumn;
  }
  if (chart?.yColumn) {
    map[chart.yColumn] = chart?.advancedConfig?.yAxisTitle || columnLabels[chart.yColumn] || chart.yColumn;
  }
  const labelCol = chart?.labelColumn || chart?.groupByColumn;
  if (labelCol) {
    // Igual que xColumn/yColumn arriba: si no hay un nombre mejor (alias de la serie),
    // se deja el propio nombre de la columna tal cual, no un "Serie" generico que le
    // pisaria un alias ya bueno (ej. "Sitio") puesto en el editor.
    map[labelCol] = columnLabels[labelCol] || labelCol;
  }
  // Comparacion por segunda fecha (customDateCompare) o alineada por dia de semana: el
  // servidor arma un dataset sintetico con claves fijas (periodo_label/serie/valor), no
  // xColumn/yColumn/labelColumn. Se reusan las mismas etiquetas ya resueltas arriba para
  // esas claves, si el gráfico las tiene; si las filas exportadas no son ese dataset
  // sintetico, applyFriendlyHeaders ignora estas entradas (no hay esas claves en la fila).
  if (map[chart?.xColumn]) map.periodo_label = map[chart.xColumn];
  if (labelCol && map[labelCol]) map.serie = map[labelCol];
  if (map[chart?.yColumn]) map.valor = map[chart.yColumn];
  return map;
}

// Postgres/mssql suelen devolver NUMERIC/BIGINT como string para no perder precisión.
// Sin esto, Excel guarda la celda como texto y no se puede sumar/filtrar como número.
const NUMERIC_STRING_RE = /^-?\d+(\.\d+)?$/;

function coerceNumericValue(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || !NUMERIC_STRING_RE.test(trimmed)) return value;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : value;
}

function isNumericCellValue(v) {
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v !== 'string') return false;
  return NUMERIC_STRING_RE.test(v.trim());
}

/**
 * Agrega una columna "Total" (suma de las columnas numéricas de columnOrder, o de todas
 * si no hay columnOrder) a cada fila, antes de traducir encabezados. Mismo cálculo que
 * "Suma por fila" en DataTable.js (rowTotals, con showTotalColumn), que hasta ahora solo
 * se mostraba en pantalla y nunca llegaba al .xlsx exportado.
 */
function addRowTotals(rows, columnOrder) {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const cols = Array.isArray(columnOrder) && columnOrder.length > 0 ? columnOrder : Object.keys(rows[0] || {});
  return rows.map((row) => {
    const total = cols.reduce((sum, col) => {
      const v = row[col];
      return isNumericCellValue(v) ? sum + parseFloat(String(v).trim()) : sum;
    }, 0);
    return { ...row, Total: total };
  });
}

/**
 * Renombra las claves de cada fila a su nombre amigable. Si dos claves quedaran
 * con el mismo nombre, la primera se queda con el nombre amigable y las demás
 * agregan un sufijo (2), (3)... para no perder columnas por colisión. De paso
 * convierte a número los valores que llegan como texto numérico.
 *
 * Devuelve { rows, headers } en vez de solo las filas: XLSX.utils.json_to_sheet NO
 * respeta el orden de insercion de claves de un objeto JS (internamente tambien
 * reordena antes las que parecen indices enteros, ej. "10".."23" antes que "00".."09"),
 * asi que el orden final hay que pasarlo aparte como opcion `header` al armar la hoja,
 * no alcanza con solo ordenar las claves de cada fila.
 *
 * columnOrder (opcional): orden explícito de columnas a usar en vez de Object.keys(rows[0]).
 * Sin esto, una tabla pivoteada (columnas "00".."23") sale desordenada en el Excel, igual
 * que ya pasaba en el header de DataTable.js sin visibleColumns (ver dateBucketPivotColumnOrder
 * en pivotDateBucketTable.js). Columnas del orden que no estén en la fila se ignoran.
 */
function applyFriendlyHeaders(rows, headerMap, columnOrder) {
  if (!Array.isArray(rows) || rows.length === 0) return { rows: rows || [], headers: [] };
  const rowKeys = new Set(Object.keys(rows[0] || {}));
  const keys =
    Array.isArray(columnOrder) && columnOrder.length > 0
      ? columnOrder.filter((k) => rowKeys.has(k))
      : Object.keys(rows[0] || {});
  const used = new Set();
  const finalNameFor = {};
  const headers = [];
  keys.forEach((key) => {
    const label = headerMap[key] || key;
    let candidate = label;
    let suffix = 2;
    while (used.has(candidate)) {
      candidate = `${label} (${suffix})`;
      suffix += 1;
    }
    used.add(candidate);
    finalNameFor[key] = candidate;
    headers.push(candidate);
  });
  const friendlyRows = rows.map((row) => {
    const next = {};
    keys.forEach((key) => {
      next[finalNameFor[key]] = coerceNumericValue(row[key]);
    });
    return next;
  });
  return { rows: friendlyRows, headers };
}

const MIN_COLUMN_WIDTH = 8;
const MAX_COLUMN_WIDTH = 60;

/** Ancho de columna (en caracteres) según el texto más largo entre el encabezado y las celdas de esa columna. */
function computeColumnWidths(rows, headers) {
  return headers.map((header) => {
    let maxLen = String(header ?? '').length;
    rows.forEach((row) => {
      const value = row[header];
      if (value == null) return;
      const len = String(value).length;
      if (len > maxLen) maxLen = len;
    });
    return { wch: Math.min(Math.max(maxLen + 2, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH) };
  });
}

/**
 * Exportado solo para tests (excelExport.test.js): arma la hoja sin pasar por el .xlsx en
 * disco. includeRowTotal (opcional): agrega la columna "Total" (ver addRowTotals) al final
 * de columnOrder, si hay uno.
 */
export function rowsToWorksheet(chart, rows, extraHeaderMap, columnOrder, includeRowTotal) {
  const headerMap = extraHeaderMap
    ? { ...friendlyHeaderMapForChart(chart), ...extraHeaderMap }
    : friendlyHeaderMapForChart(chart);
  const calcSourceKeys = Array.isArray(rows) && rows[0] ? Object.keys(rows[0]) : [];
  const calcDefs = columnOrder
    ? []
    : normalizeCalculatedColumns(chart?.advancedConfig?.tableCalculatedColumns, calcSourceKeys);
  const sourceRows = visibleRowsForExport(chart, applyCalculatedColumns(rows, calcDefs));
  const rowsWithTotal = includeRowTotal ? addRowTotals(sourceRows, columnOrder) : sourceRows;
  const effectiveColumnOrder =
    includeRowTotal && Array.isArray(columnOrder) && columnOrder.length > 0
      ? [...columnOrder, 'Total']
      : columnOrder;
  const { rows: friendlyRows, headers } = applyFriendlyHeaders(rowsWithTotal, headerMap, effectiveColumnOrder);
  const safeRows = Array.isArray(friendlyRows) ? friendlyRows : [];
  // header explicito: json_to_sheet no respeta el orden de insercion de claves de un
  // objeto JS (ver comentario en applyFriendlyHeaders), asi que sin esto el orden de
  // columnas calculado ahi se pierde igual al armar la hoja.
  const sheet = XLSX.utils.json_to_sheet(safeRows, headers.length > 0 ? { header: headers } : undefined);
  if (headers.length > 0) {
    sheet['!cols'] = computeColumnWidths(safeRows, headers);
  }
  return sheet;
}

/**
 * Descarga los datos de un solo gráfico (los mismos que se ven en su tabla/gráfico) como
 * .xlsx. extraHeaderMap (opcional) agrega/pisa etiquetas de columna que no salen de
 * chart.visualizationSpec.tableColumns, como el nombre real de la columna de fila cuando
 * las filas vienen pivoteadas desde el dataset sintético de comparación por segunda fecha
 * (ver rowHeaderLabel en dateBucketPivotConfigForChart, pivotDateBucketTable.js).
 * columnOrder (opcional): mismo orden explícito que ya usa la tabla en pantalla
 * (dateBucketPivotColumnOrder) para una tabla pivoteada, en vez del orden natural de claves.
 * includeRowTotal (opcional): agrega la columna "Total" que la tabla pivoteada siempre
 * muestra en pantalla (showTotalColumn), y que hasta ahora no llegaba al Excel.
 */
export function downloadChartExcel(chart, rows, extraHeaderMap, columnOrder, includeRowTotal) {
  const title = chartTitle(chart);
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();
  XLSX.utils.book_append_sheet(
    wb,
    rowsToWorksheet(chart, rows, extraHeaderMap, columnOrder, includeRowTotal),
    sanitizeSheetName(title, usedNames)
  );
  XLSX.writeFile(wb, `${sanitizeFileName(title)}.xlsx`);
}

/**
 * Descarga los datos de todos los gráficos de un dashboard como un solo .xlsx,
 * una pestaña por gráfico, conservando su título (saneado para las reglas de Excel).
 * Cada entrada de chartsWithData puede llevar extraHeaderMap/columnOrder/includeRowTotal,
 * iguales a los que ya recibe downloadChartExcel, para que una tabla pivoteada exporte
 * igual desde el botón de un tile individual y desde el de todo el dashboard.
 */
export function downloadDashboardExcel(dashboardTitle, chartsWithData) {
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();
  const entries = Array.isArray(chartsWithData) ? chartsWithData : [];
  entries.forEach(({ chart, rows, extraHeaderMap, columnOrder, includeRowTotal }) => {
    XLSX.utils.book_append_sheet(
      wb,
      rowsToWorksheet(chart, rows, extraHeaderMap, columnOrder, includeRowTotal),
      sanitizeSheetName(chartTitle(chart), usedNames)
    );
  });
  if (entries.length === 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([]), 'Sin datos');
  }
  XLSX.writeFile(wb, `${sanitizeFileName(dashboardTitle || 'dashboard')}.xlsx`);
}
